"use client";

import { Flower2, Leaf, Sun, Moon, Sparkles, Coffee, Tag, Users, Calendar, Bath, BookOpen, Heart, Star, Gift, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Advantage() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const categories = [{
    title: "Core Funding Engines",
    benefits: [{
      icon: Flower2,
      name: "Fix-and-Flip Capital",
      description: "High-leverage debt structured for rapid acquisition and renovation cycles."
    }, {
      icon: Bath,
      name: "BRRRR & Rental Debt",
      description: "Long-term financing designed to stabilize your portfolio and maximize cash flow."
    }, {
      icon: Wind,
      name: "New Construction",
      description: "Modular and ground-up funding for developers building the future of housing."
    }, {
      icon: Sparkles,
      name: "Gap Equity Solutions",
      description: "Second-position capital to bridge the gap, ensuring you close with minimal cash out-of-pocket."
    }]
  }, {
    title: "Strategic Partnership",
    benefits: [{
      icon: Coffee,
      name: "Joint Venture Program",
      description: "Collaborative funding for investors with proven experience or strong capital reserves."
    }, {
      icon: Tag,
      name: "Referral Partner Network",
      description: "Exclusive incentives for brokers, realtors, and contractors who refer high-quality deals."
    }, {
      icon: Heart,
      name: "Capital Partner Strength",
      description: "Access our capital partner's track record and reputation to establish credibility in the market."
    }, {
      icon: Leaf,
      name: "Architectural Underwriting",
      description: "Precision-based deal analysis that values potential as much as current assets."
    }, {
      icon: Moon,
      name: "Vault-Grade Security",
      description: "Professional‑grade transparency and data protection for every transaction."
    }]
  }, {
    title: "Operational Excellence",
    benefits: [{
      icon: Gift,
      name: "Priority Deal Flow",
      description: "First-look access to off-market opportunities and strategic capital deployments."
    }, {
      icon: Calendar,
      name: "Rapid Closing Cycles",
      description: "Streamlined underwriting processes designed to meet the speed of the market."
    }, {
      icon: Users,
      name: "Investor Community",
      description: "Access to our network of developers, lenders, and industry experts."
    }, {
      icon: BookOpen,
      name: "Market Intelligence",
      description: "Quarterly insights on capital trends and real estate market shifts."
    }, {
      icon: Star,
      name: "Strategic Advisory",
      description: "Direct access to our capital architects for complex deal structuring."
    }]
  }];
  return <section data-section-id="158" id="the-gap-equity-advantage" className="bg-background py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs tracking-[0.3em] uppercase text-primary mb-6">
            The BeneWealth Advantage
          </p>
          <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[52px] leading-tight text-foreground">
            Architecting <em className="italic text-primary">Capital.</em>
          </h2>
          <div className="w-12 h-px bg-border mx-auto mt-8" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24 overflow-hidden rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_blueprint_structure.png" alt="Architectural blueprint of a financial structure with gold accents and deep blue lighting" loading="lazy" decoding="async" className="w-full h-[400px] lg:h-[720px] object-cover" />
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-14">
              {categories.map((category, catIndex) => <div data-index={catIndex} key={catIndex}>
                  {catIndex > 0 && <div className="w-full h-px bg-border mb-14" aria-hidden="true" />}

                  <h3 className="font-default font-semibold text-2xl text-foreground mb-8 italic">
                    {category.title}
                  </h3>

                  <ul className="space-y-6">
                    {category.benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <li data-index={i} key={i} className="group flex items-start gap-5 cursor-default">
                          <div className="shrink-0 mt-1">
                            <Icon className="w-5 h-5 text-primary transition-all duration-500 ease-out group-hover:scale-125" strokeWidth={1.25} aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-default font-semibold text-lg text-foreground transition-colors duration-500 group-hover:text-primary">
                              {benefit.name}
                            </h4>
                            <p className="font-default text-sm text-muted-foreground mt-1 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>

            {}
            <div className="mt-16 pt-10 border-t border-border">
              <p className="font-default font-medium text-xl italic text-muted-foreground leading-relaxed">
                "Capital is the foundation of every great project. We provide the structural integrity required to build at scale."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}